import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router";

const useActiveTab = (tabs, controlsRef, activeTabRef) => {
  const location = useLocation();
  const [activeKey, setActiveKey] = useState(tabs[0]?.key ?? null);

  const shouldListenLocationChange = useRef(true);
  useEffect(() => {
    if (
      shouldListenLocationChange.current &&
      location.hash &&
      tabs.find(({ anchor }) => location.hash === `#${anchor}`)
    ) {
      setActiveKey(location.hash.replace(/^#/, ""));
    }
  }, [location, tabs]);

  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(({ key }) => key === activeKey)) {
      setActiveKey(tabs[0].key);
    }
    shouldListenLocationChange.current = true;
  }, [activeKey, tabs]);

  const setActiveKeyFromWithin = (key) => {
    setActiveKey(key);
    shouldListenLocationChange.current = false;
  };

  useEffect(() => {
    const isNext = (event) => event.keyCode === 39;
    const isPrevious = (event) => event.keyCode === 37;
    const isTab = (event) => event.keyCode === 9 && !event.shiftKey;
    const isPreviousTab = (event) => event.keyCode === 9 && event.shiftKey;

    const selectTab = (getNewIndex) => {
      const focusable = controlsRef.current.querySelectorAll("button");
      const currentIndex = new Array(...focusable).findIndex(
        (element) => element === document.activeElement
      );
      const nextIndex = getNewIndex(currentIndex, focusable.length);
      const nextElement = focusable[nextIndex];
      nextElement.focus();
      nextElement.click();
    };

    const listenArrowKeys = (event) => {
      if (controlsRef.current && controlsRef.current.contains(event.target)) {
        if (isNext(event)) {
          event.preventDefault();
          selectTab((currentIndex, length) => (currentIndex + 1) % length);
        } else if (isPrevious(event)) {
          event.preventDefault();
          selectTab(
            (currentIndex, length) => (currentIndex - 1 + length) % length
          );
        } else if (isTab(event) && activeTabRef.current) {
          event.preventDefault();
          activeTabRef.current.focus();
        }
        return;
      }

      if (
        isPreviousTab(event) &&
        activeTabRef.current === document.activeElement &&
        controlsRef.current
      ) {
        event.preventDefault();

        const currentIndex = tabs.findIndex(({ key }) => key === activeKey);
        const currentTabButton =
          controlsRef.current.querySelectorAll("button")[currentIndex];
        currentTabButton.focus();
        return;
      }
    };
    document.addEventListener("keydown", listenArrowKeys);

    return () => {
      document.removeEventListener("keydown", listenArrowKeys);
    };
  }, [activeKey, activeTabRef, controlsRef, tabs]);

  return [activeKey, setActiveKeyFromWithin];
};

export default useActiveTab;
